import React from 'react';
import { motion } from 'framer-motion';

const getImageSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('https://res.cloudinary.com/')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/media/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  if (url.startsWith('/uploads/')) return `${process.env.REACT_APP_BACKEND_URL}/api/media/${url.replace('/uploads/', '')}`;
  return url;
};

const isVideo = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
  return videoExtensions.some(ext => url.toLowerCase().includes(ext));
};

export const PageHero = ({ 
  title, 
  subtitle, 
  backgroundUrl,
  className = "",
  darkOverlay = true,
  darkMode = false  // Force dark theme even without background
}) => {
  const hasBackground = !!backgroundUrl;
  const bgSrc = getImageSrc(backgroundUrl);
  const isVideoBg = isVideo(bgSrc);

  // Determine text colors based on background or darkMode
  const useDarkTheme = hasBackground || darkMode;
  const titleColor = useDarkTheme ? 'text-white' : 'text-[#0B1221]';
  const subtitleColor = useDarkTheme ? 'text-white/80' : 'text-[#6E6E73]';
  const bgClass = hasBackground ? '' : (darkMode ? 'bg-[#0B1221]' : 'bg-[#F5F5F7]');

  return (
    <section className={`relative py-24 lg:py-32 overflow-hidden ${bgClass} ${className}`}>
      {/* Background Media */}
      {hasBackground && (
        <>
          {isVideoBg ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src={bgSrc} type="video/mp4" />
            </video>
          ) : (
            <div
              className="absolute inset-0 w-full h-full bg-cover bg-center"
              style={{ backgroundImage: `url(${bgSrc})` }}
            />
          )}
          
          {/* Dark Overlay for text readability */}
          {darkOverlay && (
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B1221]/70 via-[#0B1221]/60 to-[#0B1221]/70" />
          )}
        </>
      )}

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 ${titleColor}`}>
            {title}
          </h1>
          {subtitle && (
            <p className={`text-lg max-w-2xl mx-auto ${subtitleColor}`}>
              {subtitle}
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default PageHero;
